import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { isUuid } from '@leanin/shared';
import { CoachPage } from '@/components/coach';
import { GuestCoachPage } from '@/components/coach/GuestCoachPage';
import { useMember } from '@/lib/use-session';

type CoachSearch = { readonly session?: string };

// Auth-optional: members get the full coach (the `_public` layout applies
// the same onboarding gate `_authed` does), logged-out visitors the guest
// coach (docs/design/decisions/2026-10-21-guest-coach.md).
//
// No beforeLoad gate for either: access is a flag capability, not a role,
// and the degraded state is a quiet in-page "not available" (never a redirect
// loop or a teaser). CoachPage and GuestCoachPage own that branch; the server
// enforces the flag on every create/turn regardless.
//
// `session` opens one of the member's sessions on arrival — /auth/finishing
// lands a member here with the guest conversation it just claimed.
export const Route = createFileRoute('/_public/coach/')({
  validateSearch: (raw: Record<string, unknown>): CoachSearch =>
    typeof raw.session === 'string' && isUuid(raw.session) ? { session: raw.session } : {},
  component: CoachRoute,
});

function CoachRoute() {
  const member = useMember();
  const { session } = Route.useSearch();
  const navigate = useNavigate();
  if (member === null) return <GuestCoachPage />;
  return (
    <CoachPage
      initialSessionId={session ?? null}
      onInitialSessionOpened={() => void navigate({ to: '/coach', search: {}, replace: true })}
    />
  );
}
